<template>
  <div class="space-y-8">
    <!-- Sales Loading Status -->
    <div v-if="isSalesLoading" class="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
      <div class="flex items-center text-blue-700">
        <i class="fas fa-spinner fa-spin mr-3 text-blue-500"></i>
        <span class="font-medium">กำลังโหลดข้อมูลยอดขายจาก API...</span>
      </div>
      <p class="text-sm text-blue-600 mt-1">กรุณารอสักครู่ ระบบกำลังดึงข้อมูลบิลสำหรับคลัสเตอร์นี้</p>
    </div>
    
    <!-- No Sales Data Status -->
    <div v-else-if="!isSalesLoading && salesData.totalSales === 0" class="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center text-yellow-700">
          <i class="fas fa-exclamation-triangle mr-3 text-yellow-500"></i>
          <div>
            <span class="font-medium">ไม่มีข้อมูลยอดขาย</span>
            <p class="text-sm text-yellow-600 mt-1">ยังไม่พบข้อมูลบิลสำหรับคลัสเตอร์นี้</p>
          </div>
        </div>
        <button 
          @click="$emit('load-sales-data')"
          class="px-4 py-2 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
        >
          <i class="fas fa-download mr-1"></i>
          โหลดข้อมูลใหม่
        </button>
      </div>
    </div>
    
  <!-- Revenue Cards - ใช้ข้อมูลจาก Sales Aggregation -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
      <div class="revenue-card bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div class="flex items-center justify-between mb-3 sm:mb-4">
          <div class="bg-white bg-opacity-20 rounded-lg sm:rounded-xl p-2 sm:p-3">
            <i class="fas fa-chart-line text-lg sm:text-2xl"></i>
          </div>
          <div class="text-right">
            <div class="text-xs opacity-75">จำนวนบิล</div>
            <div class="text-sm font-semibold text-green-200">{{ salesData.totalBills }}</div>
          </div>
        </div>
        <div>
          <p class="text-xs sm:text-sm opacity-90 mb-1">รายได้ทั้งหมด</p>
          <p class="amount text-xl sm:text-2xl lg:text-3xl font-bold mb-2 break-words">{{ formatCurrency(salesData.totalSales) }}</p>
          <div class="flex items-center text-xs opacity-75">
            <i class="fas fa-trending-up mr-1"></i>
      <span>จากข้อมูลจริง</span>
          </div>
        </div>
      </div>

      <div class="revenue-card bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div class="flex items-center justify-between mb-3 sm:mb-4">
          <div class="bg-white bg-opacity-20 rounded-lg sm:rounded-xl p-2 sm:p-3">
            <i class="fas fa-users text-lg sm:text-2xl"></i>
          </div>
          <div class="text-right">
            <div class="text-xs opacity-75">จำนวน OM</div>
            <div class="text-sm font-semibold text-blue-200">{{ Object.keys(salesData.salesByOM || {}).length }}</div>
          </div>
        </div>
        <div>
          <p class="text-xs sm:text-sm opacity-90 mb-1">รายได้จาก OM</p>
          <p class="amount text-xl sm:text-2xl lg:text-3xl font-bold mb-2 break-words">{{ formatCurrency(getOMTotalSales()) }}</p>
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
            <span class="text-xs sm:text-sm opacity-75">{{ getOMSalesPercentage() }}% ของยอดรวม</span>
            <span class="text-xs bg-white bg-opacity-20 px-2 py-1 rounded w-fit">
              {{ Object.values(salesData.salesByOM || {}).reduce((sum, om) => sum + (om.bills?.length || 0), 0) }} บิล
            </span>
          </div>
        </div>
      </div>

      <div class="revenue-card bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div class="flex items-center justify-between mb-3 sm:mb-4">
          <div class="bg-white bg-opacity-20 rounded-lg sm:rounded-xl p-2 sm:p-3">
            <i class="fas fa-building text-lg sm:text-2xl"></i>
          </div>
          <div class="text-right">
            <div class="text-xs opacity-75">จำนวน VC</div>
            <div class="text-sm font-semibold text-purple-200">{{ Object.keys(salesData.salesByVC || {}).length }}</div>
          </div>
        </div>
        <div>
          <p class="text-xs sm:text-sm opacity-90 mb-1">รายได้จาก VC</p>
          <p class="amount text-xl sm:text-2xl lg:text-3xl font-bold mb-2 break-words">{{ formatCurrency(getVCTotalSales()) }}</p>
          <div class="flex items-center text-xs opacity-75">
            <i class="fas fa-percentage mr-1"></i>
            <span>{{ getVCSalesPercentage() }}% ของรายได้รวม</span>
          </div>
        </div>
      </div>
      
      <div class="revenue-card bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-500 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div class="flex items-center justify-between mb-3 sm:mb-4">
          <div class="bg-white bg-opacity-20 rounded-lg sm:rounded-xl p-2 sm:p-3">
            <i class="fas fa-sitemap text-lg sm:text-2xl"></i>
          </div>
          <div class="text-right">
            <div class="text-xs opacity-75">จำนวน Sub-VC</div>
            <div class="text-sm font-semibold text-orange-200">{{ Object.keys(salesData.salesBySubVC || {}).length }}</div>
          </div>
        </div>
        <div>
          <p class="text-xs sm:text-sm opacity-90 mb-1">รายได้จาก Sub-VC</p>
          <p class="amount text-xl sm:text-2xl lg:text-3xl font-bold mb-2 break-words">{{ formatCurrency(getSubVCTotalSales()) }}</p>
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
            <span class="text-xs sm:text-sm opacity-75">{{ getSubVCSalesPercentage() }}% ของยอดรวม</span>
            <span class="text-xs bg-white bg-opacity-20 px-2 py-1 rounded w-fit">
              {{ Object.values(salesData.salesBySubVC || {}).reduce((sum, subvc) => sum + (subvc.bills?.length || 0), 0) }} บิล
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- คำอธิบายการแสดงผลข้อมูล -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
      <div class="flex items-start">
        <i class="fas fa-info-circle text-blue-500 mt-0.5 sm:mt-1 mr-2 sm:mr-3 text-sm sm:text-base"></i>
        <div>
          <h4 class="font-semibold text-blue-800 mb-2 text-sm sm:text-base">คำอธิบายการแสดงผลข้อมูล</h4>
          <div class="text-xs sm:text-sm text-blue-700 space-y-1">
            <p><strong>รายได้ทั้งหมด:</strong> ยอดขายรวมจากบิลทั้งหมดในคลัสเตอร์ ({{ salesData.totalBills }} บิล)</p>
            <p><strong>รายได้จาก OM:</strong> ยอดขายจัดกลุ่มตาม Organization Manager ({{ Object.keys(salesData.salesByOM || {}).length }} กลุ่ม)</p>
            <p><strong>รายได้จาก VC:</strong> ยอดขายจัดกลุ่มตาม Value Chain ({{ Object.keys(salesData.salesByVC || {}).length }} กลุ่ม)</p>
            <p><strong>รายได้จาก Sub-VC:</strong> ยอดขายจัดกลุ่มตาม Sub Value Chain ({{ Object.keys(salesData.salesBySubVC || {}).length }} กลุ่ม)</p>
            <p class="text-xs text-blue-600 mt-2">
              <i class="fas fa-lightbulb mr-1"></i>
              หมายเหตุ: บิลแต่ละใบอาจอยู่ในหลายกลุ่มพร้อมกัน (เช่น OM และ VC เดียวกัน) ทำให้ยอดขายบางส่วนอาจซ้ำกัน
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Performance Metrics -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
      <!-- Key Performance Indicators -->
      <div class="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden">
        <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 class="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
            <i class="fas fa-chart-bar mr-2 sm:mr-3 text-blue-600"></i>
            ตัวชี้วัดประสิทธิภาพ
          </h3>
          <p class="text-xs sm:text-sm text-gray-600 mt-1">KPIs จากข้อมูลจริงของคลัสเตอร์</p>
        </div>
        <div class="p-4 sm:p-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs sm:text-sm font-medium text-gray-700">ประสิทธิภาพ OM</span>
                <span class="text-xs sm:text-sm font-bold text-green-600">{{ getOMEfficiency() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                <div class="bg-gradient-to-r from-green-400 to-green-600 h-2 sm:h-3 rounded-full transition-all duration-500" :style="`width: ${getOMEfficiency()}%`"></div>
              </div>
              <p class="text-xs text-gray-500 mt-1">{{ getActiveOMCount() }}/{{ organizationData.oms.length }} OM มีรายได้</p>
            </div>

            <div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs sm:text-sm font-medium text-gray-700">ประสิทธิภาพ VC</span>
                <span class="text-xs sm:text-sm font-bold text-blue-600">{{ getVCEfficiency() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                <div class="bg-gradient-to-r from-blue-400 to-blue-600 h-2 sm:h-3 rounded-full transition-all duration-500" :style="`width: ${getVCEfficiency()}%`"></div>
              </div>
              <p class="text-xs text-gray-500 mt-1">{{ getActiveVCCount() }}/{{ getTotalVCs() }} VC มีรายได้</p>
            </div>

            <div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs sm:text-sm font-medium text-gray-700">การครอบคลุม Sub-VC</span>
                <span class="text-xs sm:text-sm font-bold text-purple-600">{{ getSubVCCoverage() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                <div class="bg-gradient-to-r from-purple-400 to-purple-600 h-2 sm:h-3 rounded-full transition-all duration-500" :style="`width: ${getSubVCCoverage()}%`"></div>
              </div>
              <p class="text-xs text-gray-500 mt-1">{{ getVCsWithSubVCs() }}/{{ getTotalVCs() }} VC มี Sub-VC</p>
            </div>

            <div>
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs sm:text-sm font-medium text-gray-700">การเชื่อมโยง SIE/SCM/Tech</span>
                <span class="text-xs sm:text-sm font-bold text-orange-600">{{ getIntegrationLevel() }}%</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                <div class="bg-gradient-to-r from-orange-400 to-orange-600 h-2 sm:h-3 rounded-full transition-all duration-500" :style="`width: ${getIntegrationLevel()}%`"></div>
              </div>
              <p class="text-xs text-gray-500 mt-1">{{ getTotalIntegrations() }} โครงการเชื่อมโยง</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden">
    <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <h3 class="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
            <i class="fas fa-tachometer-alt mr-2 sm:mr-3 text-green-600"></i>
      สถิติจากข้อมูลจริง
          </h3>
          <p class="text-xs sm:text-sm text-gray-600 mt-1">ข้อมูลสำคัญของคลัสเตอร์</p>
        </div>
        <div class="p-4 sm:p-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-blue-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i class="fas fa-cogs text-blue-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">Operation Models</span>
                  <p class="text-xs text-gray-500">{{ getActiveOMCount() }} มีรายได้</p>
                </div>
              </div>
              <span class="text-lg sm:text-xl font-bold text-blue-600">{{ organizationData.oms.length }}</span>
            </div>

            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-green-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i class="fas fa-link text-green-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">Value Chains</span>
                  <p class="text-xs text-gray-500">{{ getActiveVCCount() }} มีรายได้</p>
                </div>
              </div>
              <span class="text-lg sm:text-xl font-bold text-green-600">{{ getTotalVCs() }}</span>
            </div>

            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-purple-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i class="fas fa-sitemap text-purple-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">Sub Value Chains</span>
                  <p class="text-xs text-gray-500">{{ getActiveSubVCCount() }} มีรายได้</p>
                </div>
              </div>
              <span class="text-lg sm:text-xl font-bold text-purple-600">{{ getTotalSubVCs() }}</span>
            </div>

            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-orange-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i :class="[
                    'text-orange-600',
                    isLoadingIntegrationData ? 'fas fa-spinner fa-spin' : 'fas fa-lightbulb'
                  ]" class="text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">SIE Projects</span>
                  <p class="text-xs text-gray-500">นวัตกรรมสังคม</p>
                </div>
              </div>
              <span :class="[
                'text-lg sm:text-xl',
                isLoadingIntegrationData ? 'text-gray-400' : 'text-orange-600'
              ]">{{ isLoadingIntegrationData ? '...' : getSIECount() }}</span>
            </div>

            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-teal-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i class="fas fa-truck text-teal-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">SCM Systems</span>
                  <p class="text-xs text-gray-500">ระบบห่วงโซ่อุปทาน</p>
                </div>
              </div>
              <span class="text-lg sm:text-xl font-bold text-teal-600">{{ getSCMCount() }}</span>
            </div>

            <div class="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
              <div class="flex items-center">
                <div class="bg-indigo-100 rounded-full p-1.5 sm:p-2 mr-2 sm:mr-3">
                  <i class="fas fa-code text-indigo-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <span class="font-medium text-gray-700 text-xs sm:text-sm">Tech Applications</span>
                  <p class="text-xs text-gray-500">แอปพลิเคชัน</p>
                </div>
              </div>
              <span class="text-lg sm:text-xl font-bold text-indigo-600">{{ getAppTechCount() }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

// Props
const props = defineProps({
  salesData: {
    type: Object,
    required: true
  },
  organizationData: {
    type: Object,
    required: true
  },
  isSalesLoading: {
    type: Boolean,
    default: false
  },
  isLoadingIntegrationData: {
    type: Boolean,
    default: false
  },
  sieData: {
    type: Array,
    default: () => []
  },
  scmData: {
    type: Array,
    default: () => []
  },
  appTechData: {
    type: Array,
    default: () => []
  },

});

// Emits
const emit = defineEmits(['load-sales-data']);

// Computed properties for organization metrics
const getTotalVCs = () => {
  return props.organizationData.oms.reduce((total, om) => {
    return total + (om.vcData ? om.vcData.length : 0);
  }, 0);
};

const getTotalSubVCs = () => {
  return props.organizationData.oms.reduce((total, om) => {
    if (!om.vcData) return total;
    return total + om.vcData.reduce((vcTotal, vc) => {
      return vcTotal + (vc.subVcData ? vc.subVcData.length : 0);
    }, 0);
  }, 0);
};

// Sales calculation functions
const getOMTotalSales = () => {
  const total = Object.values(props.salesData.salesByOM || {}).reduce((total, omSales) => {
  return total + (omSales?.totalInTHB || 0);
  }, 0);
  return total;
};

const getVCTotalSales = () => {
  const total = Object.values(props.salesData.salesByVC || {}).reduce((total, vcSales) => {
  return total + (vcSales?.totalInTHB || 0);
  }, 0);
  return total;
};

const getSubVCTotalSales = () => {
  const total = Object.values(props.salesData.salesBySubVC || {}).reduce((total, subVcSales) => {
  return total + (subVcSales?.totalInTHB || 0);
  }, 0);
  return total;
};

// Percentage calculations
const getOMSalesPercentage = () => {
  if (props.salesData.totalSales === 0) return 0;
  return ((getOMTotalSales() / props.salesData.totalSales) * 100).toFixed(1);
};

const getVCSalesPercentage = () => {
  if (props.salesData.totalSales === 0) return 0;
  return ((getVCTotalSales() / props.salesData.totalSales) * 100).toFixed(1);
};

const getSubVCSalesPercentage = () => {
  if (props.salesData.totalSales === 0) return 0;
  return ((getSubVCTotalSales() / props.salesData.totalSales) * 100).toFixed(1);
};

// Efficiency calculations
const getOMEfficiency = () => {
  const totalOMs = props.organizationData.oms.length;
  if (totalOMs === 0) return 0;
  const activeOMs = props.organizationData.oms.filter(om => getOMSales(om._id) > 0).length;
  return Math.round((activeOMs / totalOMs) * 100);
};

const getVCEfficiency = () => {
  const totalVCs = getTotalVCs();
  if (totalVCs === 0) return 0;
  let activeVCs = 0;
  props.organizationData.oms.forEach(om => {
    if (om.vcData) {
      om.vcData.forEach(vc => {
        if (getVCSales(vc._id) > 0) activeVCs++;
      });
    }
  });
  return Math.round((activeVCs / totalVCs) * 100);
};

const getSubVCCoverage = () => {
  const totalVCs = getTotalVCs();
  if (totalVCs === 0) return 0;
  let vcsWithSubVCs = 0;
  props.organizationData.oms.forEach(om => {
    if (om.vcData) {
      om.vcData.forEach(vc => {
        if (vc.subVcData && vc.subVcData.length > 0) vcsWithSubVCs++;
      });
    }
  });
  return Math.round((vcsWithSubVCs / totalVCs) * 100);
};

// Helper functions for counting active nodes
const getActiveOMCount = () => {
  return props.organizationData.oms.filter(om => getOMSales(om._id) > 0).length;
};

const getActiveVCCount = () => {
  let activeVCs = 0;
  props.organizationData.oms.forEach(om => {
    if (om.vcData) {
      om.vcData.forEach(vc => {
        if (getVCSales(vc._id) > 0) activeVCs++;
      });
    }
  });
  return activeVCs;
};

const getActiveSubVCCount = () => {
  let activeSubVCs = 0;
  props.organizationData.oms.forEach(om => {
    if (om.vcData) {
      om.vcData.forEach(vc => {
        if (vc.subVcData) {
          vc.subVcData.forEach(subvc => {
            if (getSubVCSales(subvc._id) > 0) activeSubVCs++;
          });
        }
      });
    }
  });
  return activeSubVCs;
};

const getVCsWithSubVCs = () => {
  let vcsWithSubVCs = 0;
  props.organizationData.oms.forEach(om => {
    if (om.vcData) {
      om.vcData.forEach(vc => {
        if (vc.subVcData && vc.subVcData.length > 0) vcsWithSubVCs++;
      });
    }
  });
  return vcsWithSubVCs;
};

const getTotalIntegrations = () => {
  return getSIECount() + getSCMCount() + getAppTechCount();
};

// Integration data functions
const getSIECount = () => {
  return props.sieData.length;
};

const getSCMCount = () => {
  return props.scmData.length;
};

const getAppTechCount = () => {
  return props.appTechData.length;
};

const getIntegrationLevel = () => {
  const totalNodes = props.organizationData.oms.length + getTotalVCs() + getTotalSubVCs();
  if (totalNodes === 0) return 0;
  
  const sieCount = getSIECount();
  const scmCount = getSCMCount();
  const techCount = getAppTechCount();
  const totalIntegrations = sieCount + scmCount + techCount;
  
  return Math.min(Math.round((totalIntegrations / totalNodes) * 100), 100);
};

// Sales helper functions
const getOMSales = (omId) => {
  return props.salesData.salesByOM?.[omId]?.totalInTHB || 0;
};

const getVCSales = (vcId) => {
  return props.salesData.salesByVC?.[vcId]?.totalInTHB || 0;
};

const getSubVCSales = (subVcId) => {
  return props.salesData.salesBySubVC?.[subVcId]?.totalInTHB || 0;
};

// Currency formatting
const formatCurrency = (amount) => {
  try {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  } catch {
    return '฿0';
  }
};
</script>

<style scoped>
/* Enhanced animations and transitions */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover effects */
.hover\:-translate-y-2:hover {
  transform: translateY(-8px);
}

.hover\:shadow-2xl:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Mobile responsive improvements */
@media (max-width: 768px) {
  /* Responsive grid adjustments */
  .grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-4 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  
  /* Adjust card spacing on mobile */
  .gap-6 {
    gap: 1rem;
  }
  
  .gap-8 {
    gap: 1.5rem;
  }
  
  /* Improve text readability on mobile */
  .text-3xl {
    font-size: 1.5rem;
    line-height: 2rem;
  }
  
  .text-xl {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
  
  /* Better spacing for mobile cards */
  .rounded-2xl {
    border-radius: 1rem;
  }
  
  .p-6 {
    padding: 1rem;
  }
}
</style> 